<template>
  <b-card class="graphic-report__card w-100" :class="{ 'is-default-chart': isDefaultChart }">
    <b-container fluid>
      <b-row class="no-internal-print">
        <b-col
          cols="12"
          md="6"
          order="2"
          order-md="1"
        >
          <slot name="chart-header-content" />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex justify-content-center justify-content-md-end flex-wrap"
          order="1"
          order-md="2"
        >
          <slot name="chart-header-buttons" />
          <b-button
            variant="outline-primary"
            class="my-2"
            @click="printReport"
          >
            {{ $t('buttons.print') }}
          </b-button>
        </b-col>
      </b-row>
      <slot name="chart-summary-row" />
      <b-row v-if="oscillographyChartType === oscillographyChartTypes.OSCILLOGRAPHY">
        <s-empty-list
          class="w-100"
          v-if="showEmpty"
        />
        <oscillography-chart
          v-else
          chart-id="oscillography"
          first-column-i18n-title="fields.oscillography.samples"
          :chart-data="chartData"
          :maneuver-data="maneuverData"
          :remove-outlier.sync="removeChartOutlier"
          :enable-multi-axis.sync="enableChartMultiAxis"
          :start-index-from-one="true"
          :loading="loading"
          :oscillography-chart-type="oscillographyChartType"
          :hide-maneuvers-section="hideManeuversSection"
          @toggle-outlier="getData"
          @toggle-multi-axis="getData"
        />
      </b-row>
      <b-row v-else-if="oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS">
        <s-empty-list class="w-100" v-if="showEmpty" />
        <oscillography-chart
          x-data-manager="dateTime"
          chart-id="oscillography"
          first-column-i18n-title="fields.oscillography.samples"
          :chart-data="diagnosticsChartData"
          :maneuver-data="maneuverData"
          :remove-outlier.sync="removeChartOutlier"
          :enable-multi-axis.sync="enableChartMultiAxis"
          :start-index-from-one="false"
          :loading="loading"
          :oscillography-chart-type="oscillographyChartTypes.DIAGNOSTICS"
          :hide-maneuvers-section="hideManeuversSection"
          @toggle-outlier="getData"
          @toggle-multi-axis="getData"
          @toggle-maneuver="toggleManeuver"
          @toggle-all-maneuvers="toggleAllManeuvers"
        />
      </b-row>
    </b-container>
  </b-card>
</template>

<script>
import print from '@/services/print';
import OscillographyChart from '@/components/charts/oscillography/OscillographyChart';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';

export default {
  props: {
    reportData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isDefaultChart: {
      type: Boolean,
      default: false,
    },
    oscillographyChartType: {
      type: String,
      default: oscillographyChartTypes.OSCILLOGRAPHY,
    },
    hideManeuversSection: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    OscillographyChart,
  },
  mixins: [defaultChartMixin],
  data() {
    return {
      originalDiagnosticsChartData: [],
      activeManeuvers: [],
      oscillographyChartTypes,
    };
  },
  computed: {
    chartData() {
      const filteredData = this.reportData.chartData.filter(item => Array.isArray(item.values)
      && item.values.length > 0);
      const sortedChartData = ((this.reportData && filteredData) || [])
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));

      return sortedChartData;
    },
    diagnosticsChartData() {
      if (this.activeManeuvers.length === 0) {
        return this.originalDiagnosticsChartData.map(item => ({
          ...item,
          values: [],
        }));
      }

      return this.originalDiagnosticsChartData.map(item => ({
        ...item,
        values: item.values.filter(
          v => !v.maneuver || this.activeManeuvers.includes(v.maneuver),
        ),
      }));
    },
    showEmpty() {
      return !this.chartData || !this.chartData.length;
    },
    maneuverData() {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        return this.reportData.maneuvers.filter(maneuver => {
          if (!maneuver.infoData || !maneuver.infoData.diagnostics) {
            return false;
          }
          return maneuver.infoData.diagnostics.some(field => field.hasValue);
        });
      }
      return this.reportData.maneuvers;
    },
  },
  mounted() {
    if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
      this.activeManeuvers = this.maneuverData.map(this.getManeuverKey);
    } else {
      this.activeManeuvers = this.reportData.maneuvers.map(this.getManeuverKey);
    }

    if (this.reportData.diagnosticsChartData
      && Array.isArray(this.reportData.diagnosticsChartData)) {
      this.originalDiagnosticsChartData = this.reportData.diagnosticsChartData.map(
        item => ({
          ...item,
          values: Array.isArray(item.values) ? [...item.values] : [],
        }),
      );
    } else {
      this.originalDiagnosticsChartData = [];
    }
  },
  methods: {
    getManeuverKey(maneuver) {
      return maneuver.maneuverKey || maneuver.maneuverUUID;
    },
    getData() {
      this.$emit('get-chart-data', this.chartFilters);
    },
    printReport() {
      print();
    },
    toggleManeuver(maneuverKey) {
      const index = this.activeManeuvers.indexOf(maneuverKey);
      if (index === -1) {
        this.activeManeuvers.push(maneuverKey);
      } else {
        this.activeManeuvers.splice(index, 1);
      }
    },
    toggleAllManeuvers(event) {
      if (event) {
        if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
          this.activeManeuvers = this.maneuverData.map(this.getManeuverKey);
        } else {
          this.activeManeuvers = this.reportData.maneuvers.map(this.getManeuverKey);
        }
      } else {
        this.activeManeuvers = [];
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .graphic-report
    &__card
      background-color: var(--color-background-light)

      &:not(.is-default-chart)
        padding-left: 0.5rem !important
        padding-right: 0.5rem !important
        padding-top: 0.5rem !important
        padding-bottom: 0.5rem !important
</style>
